import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  DataList,
  Dialog,
  Flex,
  Grid,
  IconButton,
  Spinner,
  Text,
  TextField
} from '@radix-ui/themes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import _ from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ImageUpload } from '~/components/ImageUpload'
import { ActionablePageLayout } from '~/components/ActionablePageLayout'
import { SectionCard } from '~/components/SectionCard'
import { updateOrg } from '~/features/orgnisations/service'
import { Tables } from '~/types'
import { toast } from 'sonner'
import { TRPCOutputs, useTRPC } from '~/utils/trpc'
import { LuCheck, LuCopy, LuMinus, LuUserPlus } from 'react-icons/lu'
import { DateTime } from 'luxon'
import { useCurrentLanguage } from '~/hooks/useCurrentLanguage'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCopyToClipboard } from 'react-use'
import { PropsWithChildren } from 'react'
import { ReactNode } from 'react'
import { useColorFromSeed } from '~/hooks/useColors'

const pickOrgData = (org: Tables<'org'>) => _.pick(org, ['name', 'icon_url'])

type DialogProps = PropsWithChildren<{}>
const AddOrgMemberDialog = ({ children }: DialogProps) => {
  const { t } = useTranslation('settings')
  const queryClient = useQueryClient()
  const [tempCreds, setTempCreds] = useState<TRPCOutputs['orgMembers']['inviteOrgMember'] | null>(null)
  const trpc = useTRPC()

  const { mutate: addMember, isPending } = useMutation({
    ...trpc.orgMembers.inviteOrgMember.mutationOptions(),
    onSuccess: (data) => {
      toast.success(t('members.addMemberDialog.success'))
      setTempCreds(data)
      queryClient.invalidateQueries({
        queryKey: trpc.orgMembers.listOrgMembers.queryKey()
      })
    }
  })

  const {
    register,
    handleSubmit,
    formState: { isValid },
    reset
  } = useForm({
    resolver: zodResolver(
      z.object({
        email: z.string().email()
      })
    ),
    mode: 'onChange'
  })

  const submit = handleSubmit(({ email }) => {
    addMember({ email, role: 'admin' })
  })

  const [, copyToClipboard] = useCopyToClipboard()

  const handleCopyPassword = () => {
    if (!tempCreds) {
      return
    }
    copyToClipboard(tempCreds.temporaryPassword)
    toast.success(t('members.addMemberDialog.passwordCopied'))
  }

  const handleAddMoreClick = () => {
    setTempCreds(null)
    reset()
  }

  return (
    <Dialog.Root>
      <Dialog.Trigger>{children}</Dialog.Trigger>
      <Dialog.Content>
        <Dialog.Title>{t('members.addMemberDialog.title')}</Dialog.Title>
        <Dialog.Description>{t('members.addMemberDialog.description')}</Dialog.Description>
        {tempCreds ? (
          <Flex mt={'5'} direction={'column'} gap="3">
            <Text size={'2'} color="gray">
              {t('members.addMemberDialog.copyPasswordWarning')}
              Copy this password and send it to the person. They will be asked to changed it at first login.
            </Text>
            <Card>
              <DataList.Root>
                <DataList.Item>
                  <DataList.Label>Email</DataList.Label>
                  <DataList.Value>{tempCreds.email}</DataList.Value>
                </DataList.Item>
                <DataList.Item>
                  <DataList.Label>{t('members.addMemberDialog.temporaryPassword')}</DataList.Label>
                  <DataList.Value>
                    <Flex gap="2" align={'center'}>
                      <Badge variant="soft" color="gray" size={'2'}>
                        {tempCreds.temporaryPassword}
                      </Badge>
                      <IconButton variant="ghost" color="gray" size={'1'} onClick={handleCopyPassword}>
                        <LuCopy size={20} />
                      </IconButton>
                    </Flex>
                  </DataList.Value>
                </DataList.Item>
              </DataList.Root>
            </Card>
            <Button className="ml-auto" variant="soft" onClick={handleAddMoreClick}>
              {t('members.addMemberDialog.addMore')}
            </Button>
          </Flex>
        ) : (
          <form onSubmit={submit}>
            <Flex direction={'column'} gap="1" mt={'4'}>
              <Text size={'2'} className="font-medium" htmlFor="email">
                E-mail
              </Text>
              <TextField.Root {...register('email')} placeholder={'user@organisation.com'} />
              <Button ml={'auto'} mt={'4'} type="submit" loading={isPending} disabled={!isValid}>
                {t('members.addMemberDialog.addMember')}
              </Button>
            </Flex>
          </form>
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}

const MemberItemGrid = ({ children }: { children: ReactNode }) => {
  return (
    <Grid
      gap="2"
      className="grid-cols-[1.5fr,repeat(3,0.7fr)] [&>.rt-Text:not(:first-child)]:text-center"
      align={'center'}
    >
      {children}
    </Grid>
  )
}

const MemberListItem = ({ member }: { member: TRPCOutputs['orgMembers']['listOrgMembers'][0] }) => {
  const locale = useCurrentLanguage()
  const avatarColor = useColorFromSeed(member.email)
  return (
    <MemberItemGrid>
      <Flex gap="3" align={'center'}>
        <Avatar
          color={avatarColor}
          variant="soft"
          radius="full"
          src={member.profile.avatarURL}
          fallback={member.email.slice(0, 2)}
          size={'1'}
        />
        <Flex direction={'column'}>
          <Text size={'2'} className="font-medium">
            {member.profile.fullName || member.email}
          </Text>
          {member.profile.fullName && (
            <Text color="gray" size={'1'}>
              {member.email}
            </Text>
          )}
        </Flex>
      </Flex>
      <Text color="gray" size={'2'}>
        {DateTime.fromISO(member.createdAt).toLocaleString(DateTime.DATE_FULL, { locale })}
      </Text>
      <Text color="gray" size={'2'}>
        {member.lastSignIn ? (
          DateTime.fromISO(member.lastSignIn).toLocaleString({ month: 'short', day: 'numeric' }, { locale })
        ) : (
          <LuMinus className="mx-auto" />
        )}
      </Text>
      <Text color="gray" size={'2'} className="m-auto">
        {member.hasChangedTempPassword ? <LuCheck /> : <LuMinus />}
      </Text>
    </MemberItemGrid>
  )
}

const Page = () => {
  const { t } = useTranslation('settings')
  const { org, queryClient } = Route.useRouteContext()
  const [data, setData] = useState(pickOrgData(org))
  const router = useRouter()
  const trpc = useTRPC()
  const isDirty = !_.isEqual(data, pickOrgData(org))

  const { data: orgMembers = [], isLoading } = useQuery(trpc.orgMembers.listOrgMembers.queryOptions())

  const { mutate: updateOrgMutation, isPending } = useMutation({
    mutationFn: () => updateOrg(org.id, data),
    onSuccess: () => {
      toast.success(t('general.orgUpdated'))
      router
        .invalidate()
        .then(() => queryClient.invalidateQueries())
        .then(() => router.invalidate())
    }
  })

  return (
    <ActionablePageLayout title={t('pageTitle')}>
      <Flex m={'auto'} gap="5" direction={'column'} className="w-full lg:w-[90%] xl:w-[70%] max-w-[1200px]">
        <SectionCard title={t('general.sectionTitle')}>
          <Box>
            <Text size={'2'}>{t('general.orgLogo')}</Text>
            <ImageUpload
              size={100}
              imageUrl={org.icon_url}
              uploadKey={`${org.id}/org-icon`}
              onUploadSuccess={(icon_url) => {
                setData((d) => ({ ...d, icon_url: icon_url }))
              }}
            />
            <Text size={'1'} color="gray">
              {t('general.logoHint')}
            </Text>
          </Box>
          <Box>
            <Text size={'2'}>{t('general.orgName')}</Text>
            <TextField.Root
              my="3"
              value={data.name}
              onChange={(e) => setData((d) => ({ ...d, name: e.target.value }))}
            />
            <Button disabled={!isDirty} onClick={() => updateOrgMutation()} loading={isPending}>
              {t('general.saveButton')}
            </Button>
          </Box>
        </SectionCard>
        <SectionCard title={t('members.sectionTitle')}>
          <Box>
            <Text size={'1'} color="gray" as="p">
              {t('members.description')}
            </Text>

            {isLoading ? (
              <Flex align={'center'} justify={'center'} my="5" className="min-h-20">
                <Spinner />
              </Flex>
            ) : (
              <>
                <Flex my={'5'}>
                  <AddOrgMemberDialog>
                    <Button className="ml-auto">
                      {t('members.addMemberButton')}
                      <LuUserPlus />
                    </Button>
                  </AddOrgMemberDialog>
                </Flex>

                {orgMembers.length > 0 && (
                  <Flex direction={'column'} gap={'4'}>
                    <MemberItemGrid>
                      <Text weight={'medium'} size={'2'} className="ml-9">
                        {t('members.memberName')}
                      </Text>
                      <Text weight={'medium'} size={'2'}>
                        {t('members.invitedOn')}
                      </Text>
                      <Text weight={'medium'} size={'2'}>
                        {t('members.lastSignIn')}
                      </Text>
                      <Text weight={'medium'} size={'2'}>
                        {t('members.hasChangedTempPassword')}
                      </Text>
                      <Box />
                    </MemberItemGrid>
                    {orgMembers.map((m) => (
                      <MemberListItem member={m} key={m.id} />
                    ))}
                  </Flex>
                )}
              </>
            )}
          </Box>
        </SectionCard>{' '}
      </Flex>
    </ActionablePageLayout>
  )
}

export const Route = createFileRoute('/settings')({
  component: Page
})
